import React, { useState, useMemo } from "react";
import { Input } from "antd";
import { notification, Select } from "antd";
import { generateYearOptions } from "@/composable/useDates";
import { getResponseErrorMessage, getApiBusinessError } from '@/utils/api-service'

import api from "@/api/index";

import CardContent from "@/components/card/CardContent";
import VoiceoverCard from "@/components/voiceover/VoiceoverCard";
import ModalVoiceover from "@/components/voiceover/ModalVoiceover";

import "./styles/serie-form.scss";

interface Props {
  serialId: number | string;
  seasonId: number | string;
  movie: any;

  showList: () => void;
}

const { TextArea } = Input;

function SerieForm({ serialId, seasonId, movie, showList }: Props) {
  const [order, setOrder] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [activeVoiceover, setActiveVoiceover] = useState(null);
  const [description, setDescription] = useState<string>("");
  const [imdb_id, set_imdb_id] = useState(movie.imdb_id);
  const [kinopoiskId, setKinopoiskId] = useState(movie.kinopoisk_id);
  const [year, setYear] = useState();

  const [voiceovers, setVoiceovers] = useState([]);

  const handleSave = (voiceover: any) => {
    if(voiceover.index === null || voiceover.index  === undefined) {
      setVoiceovers((prev) => [...prev, voiceover]);
    } else {
      const list = voiceovers.slice()
      list[voiceover.index].voiceoverName = voiceover.voiceoverName
      list[voiceover.index].voiceover_id = voiceover.voiceover_id
      list[voiceover.index].video_file = voiceover.video_file
      list[voiceover.index].name = voiceover.name
      list[voiceover.index].has_internal_ads = voiceover.has_internal_ads

      setVoiceovers(list)
    }

    setIsShowModal(false);
  };

  const handleVoiceoverClose = () => {
    setIsShowModal(false);
  };

  const showVoiceover = () => {
    setIsShowModal(true);
    setActiveVoiceover({});
  };

  const handleSaveSerie = async () => {
    try {
      const data = {
        film_name: name,
        sort_order: order,
        description: description,
        release_year: year,
        kinopoisk_id: kinopoiskId,
        imdb_id: imdb_id,
        voiceovers: voiceovers.map((voiceover) => {
          return {
            ...voiceover,
            video_file: voiceover.video_file
          };
        }),
      };

      const response = await api.serie.addSerieToSeason(serialId, seasonId, data);

      // Бэкенд отдаёт отказ как HTTP 200 + success:false —
      // показываем реальное сообщение вместо ложного «Серия создана»
      const businessError = getApiBusinessError(response);
      if (businessError) {
        notification.warning({
          message: businessError,
        });
        return
      }

      showList();

      notification.success({
        message: "Серия создана",
      });

    } catch (err) {
      console.error(err);

      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при создании серии')

      notification.error({
        message: messageFormatter
      });
    }
  };

  const onRemoveFile = (index) => {
    setVoiceovers((prev) => prev.filter((_, i) => i !== index));
  };

  const onEditFile = (index: number, data) => {
    setActiveVoiceover({
      ...data,
      index: index,
    });

    setTimeout(() => {
      setIsShowModal(true);
    }, 900);
  };

  const isDisabledSave = useMemo(() => {
    if (!Array.isArray(voiceovers) || voiceovers.length === 0) {
      return true;
    }

    if(!order) {
      return true;
    }

    return false;
  }, [order, voiceovers, imdb_id, description, name, year]);

  return (
    <div className="serie-form">
      <CardContent title="Информации о серии">
        <div className="serie-form__container">
          <div className="serie-form__info-content">
            <div className="form-group">
              <label className="form-group__label">
                Название
              </label>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Введите название эпизода"
              />
            </div>

            <div className="form-group">
              <label className="form-group__label">Порядок <span className="app-required">*</span></label>
              <Input
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value);
                }}
                placeholder="Введите порядок"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-group__label">
              Описание
            </label>
            <TextArea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
              rows={4}
              placeholder="Описание"
            />
          </div>

          <div className="block-row serie-form__import">
            <div className="form-group">
              <label className="form-group__label">
                IMDB ID
              </label>
              <Input
                value={imdb_id}
                onChange={(e) => set_imdb_id(e.target.value)}
                placeholder="IMDB ID"
              />
            </div>

            <div className="form-group">
              <label className="form-group__label">
                Kinopoisk ID
              </label>
              <Input
                value={kinopoiskId}
                onChange={(e) => setKinopoiskId(e.target.value)}
                placeholder="Kinopoisk ID"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-group__label" htmlFor={"Year"}>
              Год выпуска
            </label>
            <Select
              id={"Year"}
              className={"app-select"}
              value={year}
              onChange={(val) => setYear(val)}
              placeholder="Выберите год"
              options={generateYearOptions()}
              allowClear
            />
          </div>
        </div>
      </CardContent>

      <div className="">
        <div className="film-video-files__header app-section-header">
          <h6 className={"film-video-files__title app-section-header__title"}>
            Файлы озвучек
          </h6>
          <button className={"app-btn"} onClick={showVoiceover}>
            Добавить озвучку
          </button>
        </div>
      </div>

      {voiceovers.map((voiceover, index) => {
        return (
          <VoiceoverCard
            key={`${voiceover.voiceover_id}-${index}`}
            remove={onRemoveFile}
            index={index}
            isEdit={true}
            data={voiceover}
            edit={onEditFile}
          />
        );
      })}

      <section className="app-footer">
        <div></div>
        <button
          className={
            isDisabledSave ? "app-btn primary disabled" : "app-btn primary"
          }
          disabled={isDisabledSave}
          onClick={handleSaveSerie}
        >
          Сохранить
        </button>
      </section>

      {isShowModal && (
        <ModalVoiceover
          close={handleVoiceoverClose}
          voiceover={activeVoiceover}
          save={handleSave}
          type="serie"
        />
      )}
    </div>
  );
}

export default SerieForm;
